import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Card, Descriptions, Input, Select, Space, message, Table } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { QtyInput } from "../../components/QtyInput";
import { DocLink } from "../../components/DocLink";
import { useApi } from "../../helpers/useApi";
import { DT_FORMATS } from "../../helpers/constants";
import type { GoodsReceipt, PurchaseOrder, StockLocation } from "../../api-models";

interface DraftLine {
  poItemId: number;
  itemCode: string;
  itemName: string;
  unit?: string;
  outstanding: number;
  acceptedQty: number;
  quarantinedQty: number;
  rejectedQty: number;
  unresolvedQty: number;
}

export default function GRNDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();

  const isNew = id === "new";
  const poId = searchParams.get("poId");
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  // A delivery does not always land at the Central Warehouse, so the receiving store is
  // pickable. The server decides which stores this particular order may be received at —
  // a line bought against a site's request can only land at CW or that site — so the list
  // is fetched per PO rather than filtered here from every warehouse.
  const [receivable, setReceivable] = useState<StockLocation[]>([]);
  const [locationId, setLocationId] = useState<number>();
  const [grn, setGrn] = useState<GoodsReceipt | null>(null);

  useEffect(() => {
    if (!isNew || !poId) return;
    request<StockLocation[]>({ url: `/pos/${poId}/receipt-locations` }).then((res) => {
      setReceivable(res);
      setLocationId(res.find((l) => l.type === "CENTRAL")?.id ?? res[0]?.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, poId]);

  useEffect(() => {
    if (isNew && poId) {
      request<PurchaseOrder>({ url: `/pos/${poId}` }).then((res) => {
        setPo(res);
        setLines(
          (res.items ?? [])
            .filter((it) => it.receivedQty < it.qty * (1 + res.tolerancePct / 100))
            .map((it) => ({
              poItemId: it.id,
              itemCode: it.item?.code ?? String(it.itemId),
              itemName: it.item?.name ?? "",
              unit: it.item?.unit?.abbreviation,
              outstanding: it.qty * (1 + res.tolerancePct / 100) - it.receivedQty,
              acceptedQty: it.qty - it.receivedQty,
              quarantinedQty: 0,
              rejectedQty: 0,
              unresolvedQty: 0,
            })),
        );
      });
    } else if (!isNew) {
      request<GoodsReceipt>({ url: `/grns/${id}` }).then(setGrn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, poId]);

  async function submit() {
    if (!po) return;
    if (!deliveryNoteNo) {
      message.error("A supplier delivery note (GDN) reference is required.");
      return;
    }
    const items = lines
      .filter((l) => l.acceptedQty + l.quarantinedQty + l.rejectedQty + l.unresolvedQty > 0)
      .map((l) => ({
        poItemId: l.poItemId, acceptedQty: l.acceptedQty, quarantinedQty: l.quarantinedQty,
        rejectedQty: l.rejectedQty, unresolvedQty: l.unresolvedQty,
      }));
    if (items.length === 0) {
      message.error("Enter at least one accepted, quarantined, rejected or unresolved quantity.");
      return;
    }
    if (!locationId) {
      message.error("Choose the warehouse the goods are received into.");
      return;
    }
    const created = await request<GoodsReceipt>({ method: "POST", url: "/grns", data: { poId: po.id, locationId, deliveryNoteNo, items } });
    message.success("Goods receipt posted.");
    navigate(`/procurement/grns/${created.id}`, { replace: true });
  }

  function updateLine(poItemId: number, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.poItemId === poItemId ? { ...l, ...patch } : l)));
  }

  if (isNew) {
    return (
      <div>
        <PageHeader
          title="New Goods Receipt"
          subtitle={po ? `${po.docNo} — ${po.supplier?.name}` : undefined}
          extra={
            <Button type="primary" onClick={submit} loading={loading} disabled={!po} data-tour="grn-post">
              Post receipt
            </Button>
          }
        />
        <Card size="small" title="Lines" data-tour="grn-lines">
          <Space wrap style={{ marginBottom: 12 }}>
            <Input
              data-tour="grn-gdn"
              placeholder="Supplier delivery note (GDN) no. — required"
              value={deliveryNoteNo}
              onChange={(e) => setDeliveryNoteNo(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              style={{ width: 260 }}
              placeholder="Receive into…"
              options={receivable.map((l) => ({ value: l.id, label: l.name }))}
              value={locationId}
              onChange={setLocationId}
            />
          </Space>
          <Table
            rowKey="poItemId"
            dataSource={lines}
            pagination={false}
            columns={[
              { title: "Code", dataIndex: "itemCode" },
              { title: "Item", dataIndex: "itemName" },
              { title: "Outstanding (incl. tolerance)", render: (_, r) => `${r.outstanding.toFixed(3)} ${r.unit ?? ""}` },
              {
                title: "Accepted",
                render: (_, r) => <QtyInput value={r.acceptedQty} unit={r.unit} onChange={(v) => updateLine(r.poItemId, { acceptedQty: v ?? 0 })} />,
              },
              {
                title: "Quarantined (damaged)",
                render: (_, r) => <QtyInput value={r.quarantinedQty} unit={r.unit} onChange={(v) => updateLine(r.poItemId, { quarantinedQty: v ?? 0 })} />,
              },
              {
                title: "Rejected",
                render: (_, r) => <QtyInput value={r.rejectedQty} unit={r.unit} onChange={(v) => updateLine(r.poItemId, { rejectedQty: v ?? 0 })} />,
              },
              {
                title: "Unresolved",
                render: (_, r) => <QtyInput value={r.unresolvedQty} unit={r.unit} onChange={(v) => updateLine(r.poItemId, { unresolvedQty: v ?? 0 })} />,
              },
            ]}
          />
        </Card>
      </div>
    );
  }

  if (!grn) return null;

  const grnColumns = [
    { title: "Code", render: (_: unknown, r: NonNullable<GoodsReceipt["items"]>[number]) => r.item?.code },
    { title: "Item", render: (_: unknown, r: NonNullable<GoodsReceipt["items"]>[number]) => r.item?.name },
    { title: "Accepted", dataIndex: "acceptedQty" },
    { title: "Quarantined", dataIndex: "quarantinedQty" },
    { title: "Rejected", dataIndex: "rejectedQty" },
    { title: "Unresolved", dataIndex: "unresolvedQty" },
  ];

  return (
    <div>
      <PageHeader title={grn.docNo} subtitle={grn.supplier?.name} extra={<Button onClick={() => window.print()}>Print</Button>} />
      <Card size="small" style={{ marginBottom: 16 }} className="glut-no-print">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Purchase order">
            <DocLink docType="PO" id={grn.poId} docNo={grn.po?.docNo} fallback="-" />
          </Descriptions.Item>
          {/* The requisition is the PO's — a GRN reaches it through the order it settles. */}
          <Descriptions.Item label="Requisition">
            <DocLink docType="PR" id={grn.po?.prId} docNo={grn.po?.pr?.docNo} fallback="-" />
          </Descriptions.Item>
          <Descriptions.Item label="Delivery note">{grn.deliveryNoteNo}</Descriptions.Item>
          <Descriptions.Item label="Received into">{grn.location?.name ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Received">{dayjs(grn.receivedAt).format(DT_FORMATS.date)}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Table rowKey="id" dataSource={grn.items ?? []} pagination={false} columns={grnColumns} />
      <DocumentPrintSheet
        title="Goods Receipt Note"
        docNo={grn.docNo}
        meta={[
          { label: "PO", value: grn.po?.docNo ?? "" },
          { label: "Supplier", value: grn.supplier?.name ?? "" },
          { label: "Delivery note", value: grn.deliveryNoteNo },
          { label: "Received into", value: grn.location?.name ?? "" },
          { label: "Date", value: dayjs(grn.receivedAt).format(DT_FORMATS.date) },
        ]}
      >
        <Table rowKey="id" dataSource={grn.items ?? []} pagination={false} columns={grnColumns} />
      </DocumentPrintSheet>
    </div>
  );
}
